import React, { useEffect, useState, useRef } from 'react';
import API from '../api';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { priceToCode, getPriceCodeSettings, isBarcodeOnly } from '../utils/priceCode';

const CreateInvoice = () => {
  const [products, setProducts] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [items, setItems] = useState([{ product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
  const [searches, setSearches] = useState(['']);
  const [showDropdown, setShowDropdown] = useState({});
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [showAddMore, setShowAddMore] = useState(false);
  const [addMoreItems, setAddMoreItems] = useState([{ product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
  const [addMoreSearches, setAddMoreSearches] = useState(['']);
  const [showAddMoreDropdown, setShowAddMoreDropdown] = useState({});
  const [scanning, setScanning] = useState(false);
  const [scanIndex, setScanIndex] = useState(0);
  const scannerRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlType = searchParams.get('type');
  const urlCustomerId = searchParams.get('customer_id');
  const urlCustomerName = searchParams.get('customer_name');
  const urlEmployeeId = searchParams.get('employee_id');
  const urlEmployeeName = searchParams.get('employee_name');

  const [customers, setCustomers] = useState([]);
  const [isCreditSale, setIsCreditSale] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  const [isEmployeeSale, setIsEmployeeSale] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);

  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');

  const fetchProducts = async () => {
    try {
      const res = await API.get('/products');
      setProducts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await API.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await API.get('/hr/employees');
      setEmployees(res.data.filter(e => e.is_active));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    fetchEmployees();
    if (urlType === 'credit' && urlCustomerId) {
      setIsCreditSale(true);
      setSelectedCustomerId(urlCustomerId);
      setCustomerName(urlCustomerName || '');
    }
    if (urlType === 'cash' && urlCustomerId) {
      setSelectedCustomerId(urlCustomerId);
      setCustomerName(urlCustomerName || '');
    }
    if (urlType === 'employee' && urlEmployeeId) {
      setIsEmployeeSale(true);
      setSelectedEmployeeId(urlEmployeeId);
      setCustomerName(urlEmployeeName || '');
    }
  }, []);

  const startScanner = (index) => {
    setScanIndex(index);
    setScanning(true);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => {
    if (scanning) {
      import('html5-qrcode').then(({ Html5Qrcode }) => {
        const scanner = new Html5Qrcode('qr-reader');
        scannerRef.current = scanner;
        scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            stopScanner();
            try {
              const res = await API.get(`/products/barcode/${decodedText}`);
              const product = res.data;
              const alreadyUsedQty = items.reduce((sum, item, i) => {
                if (i !== scanIndex && item.product_id === product.id) return sum + item.quantity;
                return sum;
              }, 0);
              const availableStock = product.stock - alreadyUsedQty;
              if (availableStock <= 0) {
                toast.error(`No stock available for ${product.name}!`);
                return;
              }
              const newItems = [...items];
              newItems[scanIndex].product_id = product.id;
              newItems[scanIndex].price = parseFloat(product.selling_price);
              newItems[scanIndex].actual_price = parseFloat(product.selling_price);
              newItems[scanIndex].stock = availableStock;
              newItems[scanIndex].name = product.name;
              setItems(newItems);
              const newSearches = [...searches];
              newSearches[scanIndex] = product.name;
              setSearches(newSearches);
              toast.success(`${product.name} scanned successfully!`);
            } catch (err) {
              toast.error('Product not found for this barcode!');
            }
          },
          () => {}
        ).catch(() => {
          toast.error('Camera access denied!');
          setScanning(false);
        });
      });
    }
    return () => {
      if (scannerRef.current) scannerRef.current.stop().catch(() => {});
    };
  }, [scanning]);

  const handleSearchChange = (index, value) => {
    const newSearches = [...searches];
    newSearches[index] = value;
    setSearches(newSearches);
    setShowDropdown({ ...showDropdown, [index]: true });
  };

  const handleSelectProduct = (index, product) => {
    const alreadyUsedQty = items.reduce((sum, item, i) => {
      if (i !== index && item.product_id === product.id) return sum + item.quantity;
      return sum;
    }, 0);
    const availableStock = product.stock - alreadyUsedQty;
    if (availableStock <= 0) {
      toast.error(`No more stock available for ${product.name}!`);
      return;
    }
    const newItems = [...items];
    newItems[index].product_id = product.id;
    newItems[index].price = parseFloat(product.selling_price);
    newItems[index].actual_price = parseFloat(product.selling_price);
    newItems[index].stock = availableStock;
    newItems[index].name = product.name;
    setItems(newItems);
    const newSearches = [...searches];
    newSearches[index] = product.name;
    setSearches(newSearches);
    setShowDropdown({ ...showDropdown, [index]: false });
  };

  const handleQuantityChange = (index, value) => {
    const newItems = [...items];
    const qty = parseInt(value);
    const maxAllowed = newItems[index].stock;
    if (qty > maxAllowed) {
      toast.error(`Only ${maxAllowed} items available in stock!`);
      newItems[index].quantity = maxAllowed;
    } else {
      newItems[index].quantity = qty;
    }
    setItems(newItems);
  };

  const handleActualPriceChange = (index, value) => {
    const newItems = [...items];
    const newPrice = parseFloat(value) || 0;
    newItems[index].actual_price = newPrice;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
    setSearches([...searches, '']);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
    setSearches(searches.filter((_, i) => i !== index));
  };

  const totalAmount = items.reduce((sum, item) => {
    const finalPrice = item.actual_price > 0 ? item.actual_price : item.price;
    return sum + finalPrice * item.quantity;
  }, 0);

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    for (const item of items) {
      if (!item.product_id) {
        toast.error('Please select a product for all items');
        return;
      }
      if (item.quantity < 1) {
        toast.error('Quantity must be at least 1');
        return;
      }
    }
    if (isCreditSale && !selectedCustomerId) {
      toast.error('Please select a customer for credit sale');
      return;
    }
    if (isEmployeeSale && !selectedEmployeeId) {
      toast.error('Please select an employee');
      return;
    }
    setSubmitting(true);
    try {
      const res = await API.post('/invoices', {
        customer_name: customerName || 'Walk-in Customer',
        items: items.map(item => ({
          ...item,
          actual_price: item.actual_price > 0 ? item.actual_price : item.price,
        })),
        is_credit_sale: isCreditSale,
        customer_id: selectedCustomerId ? parseInt(selectedCustomerId) : null,
        is_employee_sale: isEmployeeSale,
        employee_id: isEmployeeSale ? parseInt(selectedEmployeeId) : null,
      });
      toast.success(
        isEmployeeSale ? 'Employee purchase recorded and added to ledger!' :
        isCreditSale ? 'Credit invoice created!' :
        'Invoice created!'
      );
      await fetchProducts();
      setCreatedInvoice(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create invoice');
    }
    setSubmitting(false);
  };

  const [submittingAddMore, setSubmittingAddMore] = useState(false);

  const handleAddMoreItems = async () => {
    if (submittingAddMore) return;
    for (const item of addMoreItems) {
      if (!item.product_id) {
        toast.error('Please select a product for all items');
        return;
      }
    }
    setSubmittingAddMore(true);
    try {
      const res = await API.put(`/invoices/${createdInvoice.id}/add-items`, {
        items: addMoreItems.map(item => ({
          ...item,
          actual_price: item.actual_price > 0 ? item.actual_price : item.price,
        }))
      });
      toast.success('Items added to invoice!');
      await fetchProducts();
      setCreatedInvoice(res.data);
      setShowAddMore(false);
      setAddMoreItems([{ product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
      setAddMoreSearches(['']);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add items');
    }
    setSubmittingAddMore(false);
  };

  const handlePrint = () => window.print();

  const handleWhatsAppShare = (phoneNumber) => {
    const shop = JSON.parse(localStorage.getItem('user'));
    const profile = JSON.parse(localStorage.getItem('shopProfile'));
    let message = `${shop?.shop_name}\n`;
    if (profile?.address) message += `${profile.address}\n`;
    if (profile?.phone) message += `Tel: ${profile.phone}\n`;
    if (profile?.whatsapp) message += `WhatsApp: ${profile.whatsapp}\n`;
    message += `--------------------------------\n`;
    message += `Invoice: #${createdInvoice.invoice_number || createdInvoice.id}\n`;
    message += `Date: ${new Date(createdInvoice.created_at).toLocaleDateString()}\n`;
    if (createdInvoice.customer_name !== 'Walk-in Customer') {
      message += `Customer: ${createdInvoice.customer_name}\n`;
    }
    message += `--------------------------------\n`;
    createdInvoice.items?.forEach((item, index) => {
      const actualPrice = parseFloat(item.actual_price) > 0 ? parseFloat(item.actual_price) : parseFloat(item.price);
      const isOffer = actualPrice < parseFloat(item.price);
      message += `${index + 1}. ${item.name}\n`;
      message += `   Qty: ${item.quantity} x LKR ${actualPrice.toFixed(2)}`;
      message += ` = LKR ${(actualPrice * item.quantity).toFixed(2)}`;
      if (isOffer) message += ` (Special Price)`;
      message += `\n`;
    });
    message += `--------------------------------\n`;
    message += `Total: LKR ${parseFloat(createdInvoice.total_amount).toFixed(2)}\n`;
    message += `--------------------------------\n`;
    message += `Thank you for your purchase!\n`;
    message += `Visit us again.`;
    const cleanPhone = phoneNumber.replace(/\s+/g, '').replace(/^0/, '94');
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  if (createdInvoice) {
    return (
      <div>
        <Navbar />
        <style>{`
          @media print {
            body * { visibility: hidden; }
            #bill, #bill * { visibility: visible; }
            #bill { position: absolute; left: 0; top: 0; width: 100%; }
            .no-print { display: none; }
            @page { margin: 0; size: auto; }
          }
          @media print and (max-width: 100mm) {
            #bill { width: 100% !important; font-size: 11px; }
          }
        `}</style>
        <div style={styles.container} className="container">
<div id="bill" style={{
              ...styles.bill,
              ...(JSON.parse(localStorage.getItem('shopProfile') || '{}').printer_size === '57mm' 
                ? { maxWidth: '57mm', padding: '2mm', fontSize: '10px' } 
                : JSON.parse(localStorage.getItem('shopProfile') || '{}').printer_size === '79mm'
                ? { maxWidth: '79mm', padding: '3mm', fontSize: '11px' }
                : {})
            }}>            <div style={styles.billTop}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                {JSON.parse(localStorage.getItem('shopProfile') || '{}')?.logo_url && (
                  <img src={JSON.parse(localStorage.getItem('shopProfile') || '{}').logo_url} alt="Logo" style={{ maxHeight: '40px', maxWidth: '80px' }} />
                )}
<h2 style={{ ...styles.billTitle, margin: 0 }}>{JSON.parse(localStorage.getItem('user'))?.shop_name}</h2>              </div>
              {JSON.parse(localStorage.getItem('shopProfile'))?.address && (
                <p style={styles.billSub}>📍 {JSON.parse(localStorage.getItem('shopProfile'))?.address}</p>
              )}
              {JSON.parse(localStorage.getItem('shopProfile'))?.phone && (
                <p style={styles.billSub}>📞 {JSON.parse(localStorage.getItem('shopProfile'))?.phone}</p>
              )}
              {JSON.parse(localStorage.getItem('shopProfile'))?.whatsapp && (
                <p style={styles.billSub}>💬 WhatsApp: {JSON.parse(localStorage.getItem('shopProfile'))?.whatsapp}</p>
              )}
              <div style={styles.billDividerTop}></div>
              <p style={styles.billSub}>Sales Receipt</p>
              <p style={styles.billSub}>Invoice #{createdInvoice.invoice_number || createdInvoice.id}</p>
              <p style={styles.billSub}>Date: {new Date(createdInvoice.created_at).toLocaleDateString()}</p>
{createdInvoice.customer_name && createdInvoice.customer_name !== 'Walk-in Customer' && (
  <p style={styles.billSub} className="no-print">Customer: <strong>{createdInvoice.customer_name}</strong></p>
)}           
       </div>
            <table style={styles.billTable}>
              <thead>
                <tr>
                  <th style={styles.bth}>Product</th>
                  <th style={styles.bth}>Qty</th>
                  <th style={styles.bth}>Price</th>
                  <th style={styles.bth}>Total</th>
                </tr>
              </thead>
              <tbody>
                {createdInvoice.items?.map((item, i) => {
                  const originalPrice = parseFloat(item.price);
                  const actualPrice = parseFloat(item.actual_price) > 0 ? parseFloat(item.actual_price) : originalPrice;
                  const isOffer = actualPrice < originalPrice;
                  return (
                    <tr key={i}>
                      <td style={styles.btd}>
                        {item.name}
                        {isOffer && (
                          <span style={{ display: 'block', fontSize: '11px', color: '#16a34a', fontWeight: 'bold' }}>
                            🏷️ Special Price! Save LKR {((originalPrice - actualPrice) * item.quantity).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td style={styles.btd}>{item.quantity}</td>
                      <td style={styles.btd}>
                        {isOffer && (
                          <span style={{ textDecoration: 'line-through', color: '#888', fontSize: '11px', display: 'block' }}>
                            LKR {originalPrice.toFixed(2)}
                          </span>
                        )}
                        <span style={{ color: isOffer ? '#16a34a' : 'inherit', fontWeight: isOffer ? 'bold' : 'normal' }}>
                          LKR {actualPrice.toFixed(2)}
                        </span>
                      </td>
                      <td style={styles.btd}>LKR {(actualPrice * item.quantity).toFixed(2)}</td>
                    </tr>
                  );
                })}
             </tbody>
            </table>
            <div style={styles.billTotal}>
              <span>Total Amount:</span>
              <span style={styles.billTotalAmount}>LKR {parseFloat(createdInvoice.total_amount).toFixed(2)}</span>
            </div>

            {!createdInvoice.is_credit_sale && Math.abs(parseFloat(createdInvoice.total_amount) - parseFloat(createdInvoice.amount_paid || 0)) > 0.01 && (
              <div style={styles.returnSummaryRow || { display: 'flex', justifyContent: 'space-between', padding: '4px 0' }} className="no-print">
                <span>{parseFloat(createdInvoice.total_amount) > parseFloat(createdInvoice.amount_paid || 0) ? 'Amount Due from Customer:' : 'Refund to Customer:'}</span>
                <span>LKR {Math.abs(parseFloat(createdInvoice.total_amount) - parseFloat(createdInvoice.amount_paid || 0)).toFixed(2)}</span>
              </div>
            )}
            <div style={styles.billFooterSection}>
              <div style={styles.billDivider}></div>
              <p style={styles.billNote}>📋 <strong>Terms & Conditions:</strong></p>
              <ul style={styles.billTerms}>
                <li>Products can be exchanged within <strong>7 days</strong> of purchase.</li>
                <li>Items must be unused, undamaged and in original condition.</li>
                <li>Please bring this receipt for any exchange or return.</li>
                <li>No cash refund after purchase is confirmed.</li>
              </ul>
              <div style={styles.billDivider}></div>
              <p style={styles.billThankYou}>🙏 Thank you for shopping with us!</p>
              <p style={styles.billVisitAgain}>We look forward to seeing you again.</p>
            </div>
          </div>
          <div style={styles.billActions} className="no-print">
            <button onClick={handlePrint} style={styles.printBtn}>🖨️ Print Bill</button>
            <button onClick={() => setShowWhatsApp(!showWhatsApp)} style={styles.whatsappBtn}>💬 WhatsApp</button>
            <button onClick={() => setShowAddMore(true)} style={styles.addMoreBtn}>+ Add More Items</button>
            <button onClick={() => {
              setCreatedInvoice(null);
              setCustomerName('');
              setIsCreditSale(false);
              setSelectedCustomerId('');
              setIsEmployeeSale(false);
              setSelectedEmployeeId('');
              setItems([{ product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
              setSearches(['']);
            }} style={styles.newBtn}>+ New Invoice</button>
          </div>

          {showWhatsApp && (
            <div style={styles.whatsappBox} className="no-print">
              <h4 style={styles.whatsappTitle}>💬 Share Bill on WhatsApp</h4>
              <div style={styles.whatsappRow}>
                <input
                  style={styles.whatsappInput}
                  type="tel"
                  placeholder="Customer phone (e.g. 0771234567)"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                />
                <button
                  onClick={() => {
                    if (!whatsappNumber) {
                      toast.error('Please enter customer phone number');
                      return;
                    }
                    handleWhatsAppShare(whatsappNumber);
                  }}
                  style={styles.whatsappSendBtn}
                >
                  Send 💬
                </button>
              </div>
            </div>
          )}

          {showAddMore && (
            <div style={styles.addMoreSection}>
              <h3 style={styles.subtitle}>Add More Items to Invoice #{createdInvoice.invoice_number || createdInvoice.id}</h3>
              {addMoreItems.map((item, index) => (
                <div key={index} style={styles.itemBlock}>
                  <div style={styles.searchWrapper}>
                    <input
                      style={styles.input}
                      type="text"
                      placeholder="Search product..."
                      value={addMoreSearches[index]}
                      onChange={(e) => {
                        const s = [...addMoreSearches];
                        s[index] = e.target.value;
                        setAddMoreSearches(s);
                        setShowAddMoreDropdown({ ...showAddMoreDropdown, [index]: true });
                      }}
                      autoComplete="off"
                    />
                    {showAddMoreDropdown[index] && addMoreSearches[index] && (
                      <div style={styles.dropdown}>
                        {products
                          .filter((p) => (p.name.toLowerCase().includes(addMoreSearches[index].toLowerCase()) || (p.barcode && p.barcode.includes(addMoreSearches[index]))) && p.stock > 0)
                          .map((p) => (
                            <div key={p.id} style={styles.dropdownItem} onClick={() => {
                              const alreadyUsedQty = addMoreItems.reduce((sum, itm, i) => {
                                if (i !== index && itm.product_id === p.id) return sum + itm.quantity;
                                return sum;
                              }, 0);
                              const availableStock = p.stock - alreadyUsedQty;
                              if (availableStock <= 0) {
                                toast.error(`No more stock available for ${p.name}!`);
                                return;
                              }
                              const newItems = [...addMoreItems];
                              newItems[index].product_id = p.id;
                              newItems[index].price = parseFloat(p.selling_price);
                              newItems[index].actual_price = parseFloat(p.selling_price);
                              newItems[index].stock = availableStock;
                              newItems[index].name = p.name;
                              setAddMoreItems(newItems);
                              const s = [...addMoreSearches];
                              s[index] = p.name;
                              setAddMoreSearches(s);
                              setShowAddMoreDropdown({ ...showAddMoreDropdown, [index]: false });
                            }}>
                              <span>{p.name}</span>
                              <span style={styles.dropdownMeta}>
                                Sell: LKR {parseFloat(p.selling_price).toFixed(2)} | Buy: {getPriceCodeSettings().enabled && !isBarcodeOnly() ? <strong style={{color: '#7c3aed', letterSpacing: '1px'}}>{priceToCode(p.buying_price)}</strong> : `LKR ${parseFloat(p.buying_price).toFixed(2)}`} | Stock: {p.stock - addMoreItems.reduce((sum, itm) => itm.product_id === p.id ? sum + itm.quantity : sum, 0)}
                              </span>
                            </div>
                          ))}
                        {products.filter((p) => (p.name.toLowerCase().includes(addMoreSearches[index].toLowerCase()) || (p.barcode && p.barcode.includes(addMoreSearches[index]))) && p.stock > 0).length === 0 && (
                          <div style={styles.dropdownEmpty}>No products found</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={styles.itemRow}>
                    {item.product_id && (
                      <span style={styles.selectedProduct}>✅ {item.name} | Stock: {item.stock}</span>
                    )}
                    <input
                      style={styles.qtyInput}
                      type="number"
                      min="1"
                      max={item.stock}
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => {
                        const newItems = [...addMoreItems];
                        const qty = parseInt(e.target.value);
                        if (qty > item.stock) {
                          toast.error(`Only ${item.stock} items available!`);
                          newItems[index].quantity = item.stock;
                        } else {
                          newItems[index].quantity = qty;
                        }
                        setAddMoreItems(newItems);
                      }}
                    />
                    {item.product_id && (
                      <div style={styles.dynamicPriceWrapper}>
                        <input
                          style={item.actual_price > 0 && item.actual_price < item.price ? styles.dynamicPriceInputOffer : styles.dynamicPriceInput}
                          type="number"
                          min="0"
                          placeholder={`Selling: LKR ${item.price}`}
                          value={item.actual_price > 0 ? item.actual_price : ''}
                          onChange={(e) => {
                            const newItems = [...addMoreItems];
                            newItems[index].actual_price = parseFloat(e.target.value) || 0;
                            setAddMoreItems(newItems);
                          }}
                        />
                        {item.product_id && (
                          <span style={styles.buyingPriceHint}>
                            💰 Buying: {(() => {
                              const product = products.find(p => p.id === item.product_id);
                              if (!product) return '0.00';
                              return getPriceCodeSettings().enabled && !isBarcodeOnly()
                                ? <strong style={{ color: '#7c3aed', letterSpacing: '2px' }}>{priceToCode(product.buying_price)}</strong>
                                : `LKR ${parseFloat(product.buying_price).toFixed(2)}`;
                            })()}
                          </span>
                        )}
                        {item.actual_price < item.price && item.actual_price > 0 && (
                          <span style={styles.offerBadge}>🏷️ Save LKR {(item.price - item.actual_price).toFixed(2)}</span>
                        )}
                      </div>
                    )}
                    <span style={styles.itemTotal}>LKR {((item.actual_price || item.price) * item.quantity).toFixed(2)}</span>
                    {addMoreItems.length > 1 && (
                      <button type="button" onClick={() => {
                        setAddMoreItems(addMoreItems.filter((_, i) => i !== index));
                        setAddMoreSearches(addMoreSearches.filter((_, i) => i !== index));
                      }} style={styles.removeBtn}>✕</button>
                    )}
                  </div>
                </div>
              ))}
              <button onClick={() => {
                setAddMoreItems([...addMoreItems, { product_id: '', quantity: 1, price: 0, actual_price: 0, stock: 0, name: '' }]);
                setAddMoreSearches([...addMoreSearches, '']);
              }} style={styles.addItemBtn}>+ Add Item</button>
<button onClick={handleAddMoreItems} style={{ ...styles.submitBtn, opacity: submittingAddMore ? 0.6 : 1 }} disabled={submittingAddMore}>
                {submittingAddMore ? 'Updating...' : 'Update Invoice'}
              </button>            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      {scanning && (
        <div style={styles.scanOverlay}>
          <div style={styles.scanModal}>
            <h3 style={{ marginBottom: '1rem' }}>📷 Scan Barcode</h3>
            <div id="qr-reader" style={{ width: '100%' }}></div>
            <button onClick={stopScanner} style={{ ...styles.submitBtn, marginTop: '1rem', background: '#ef4444' }}>
              Cancel Scan
            </button>
          </div>
        </div>
      )}
      <div style={styles.container} className="container">
        <h2 style={styles.title}>Create New Invoice</h2>
        <div style={styles.card}>
          {isEmployeeSale && (
            <div style={styles.specialInvoiceBanner}>
              👨‍💼 Employee Purchase for: {customerName}
            </div>
          )}
          {!isEmployeeSale && selectedCustomerId && (
            <>
              <div style={styles.specialInvoiceBanner}>
                Invoice for: {customerName}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <button type="button" onClick={() => setIsCreditSale(true)}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: isCreditSale ? '2px solid #4f46e5' : '1px solid #ddd', background: isCreditSale ? '#e0e7ff' : '#fff', color: isCreditSale ? '#4f46e5' : '#666', fontWeight: isCreditSale ? '600' : '400', cursor: 'pointer', fontSize: '14px' }}>
                  💳 Credit (pay later)
                </button>
                <button type="button" onClick={() => setIsCreditSale(false)}
                  style={{ flex: 1, padding: '10px', borderRadius: '8px', border: !isCreditSale ? '2px solid #16a34a' : '1px solid #ddd', background: !isCreditSale ? '#dcfce7' : '#fff', color: !isCreditSale ? '#16a34a' : '#666', fontWeight: !isCreditSale ? '600' : '400', cursor: 'pointer', fontSize: '14px' }}>
                  💵 Paid in full now (cash)
                </button>
              </div>
            </>
          )}
          <input
            style={styles.input}
            type="text"
            placeholder="Customer Name (optional — for records only)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            disabled={!!selectedCustomerId || isEmployeeSale}
          />



          <h3 style={styles.subtitle}>Items</h3>
          {items.map((item, index) => (
            <div key={index} style={styles.itemBlock}>
              <div style={styles.searchWrapper}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                    type="text"
                    placeholder="Search product..."
                    value={searches[index]}
                    onChange={(e) => handleSearchChange(index, e.target.value)}
                    onFocus={() => setShowDropdown({ ...showDropdown, [index]: true })}
                    autoComplete="off"
                  />
                  <button type="button" onClick={() => startScanner(index)} style={styles.scanBtn}>
                    📷 Scan
                  </button>
                </div>
                {showDropdown[index] && searches[index] && (
                  <div style={styles.dropdown}>
                    {products
                      .filter((p) => (p.name.toLowerCase().includes(searches[index].toLowerCase()) || (p.barcode && p.barcode.includes(searches[index]))) && p.stock > 0)
                      .map((p) => (
                        <div key={p.id} style={styles.dropdownItem} onClick={() => handleSelectProduct(index, p)}>
                          <span>{p.name}</span>
                          <span style={styles.dropdownMeta}>
                            Sell: LKR {parseFloat(p.selling_price).toFixed(2)} | Buy: {getPriceCodeSettings().enabled && !isBarcodeOnly() ? <strong style={{color: '#7c3aed', letterSpacing: '1px'}}>{priceToCode(p.buying_price)}</strong> : `LKR ${parseFloat(p.buying_price).toFixed(2)}`} | Stock: {p.stock - items.reduce((sum, itm, i) => i !== index && itm.product_id === p.id ? sum + itm.quantity : sum, 0)}
                          </span>
                        </div>
                      ))}
                    {products.filter((p) => (p.name.toLowerCase().includes(searches[index].toLowerCase()) || (p.barcode && p.barcode.includes(searches[index]))) && p.stock > 0).length === 0 && (
                      <div style={styles.dropdownEmpty}>No products found</div>
                    )}
                  </div>
                )}
              </div>
              <div style={styles.itemRow}>
                {item.product_id && (
                  <span style={styles.selectedProduct}>
                    ✅ {item.name} | Stock: {item.stock} | Fixed: LKR {item.price.toFixed(2)}
                  </span>
                )}
                <input
                  style={styles.qtyInput}
                  type="number"
                  min="1"
                  max={item.stock}
                  placeholder="Qty"
                  value={item.quantity}
                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                  required
                />
                {item.product_id && (
                  <div style={styles.dynamicPriceWrapper}>
                    <input
                      style={item.actual_price > 0 && item.actual_price < item.price ? styles.dynamicPriceInputOffer : styles.dynamicPriceInput}
                      type="number"
                      min="0"
                      placeholder={`Selling: LKR ${item.price}`}
                      value={item.actual_price > 0 ? item.actual_price : ''}
                      onChange={(e) => handleActualPriceChange(index, e.target.value)}
                    />
                    <span style={styles.buyingPriceHint}>
                      💰 Buying: {(() => {
                        const product = products.find(p => p.id === item.product_id);
                        if (!product) return '0.00';
                        return getPriceCodeSettings().enabled && !isBarcodeOnly()
                          ? <strong style={{ color: '#7c3aed', letterSpacing: '2px' }}>{priceToCode(product.buying_price)}</strong>
                          : `LKR ${parseFloat(product.buying_price).toFixed(2)}`;
                      })()}
                    </span>
                    {item.actual_price > 0 && item.actual_price < item.price && (
                      <span style={styles.offerBadge}>🏷️ Offer! Save LKR {(item.price - item.actual_price).toFixed(2)}</span>
                    )}
                  </div>
                )}
                <span style={styles.itemTotal}>
                  LKR {((item.actual_price > 0 ? item.actual_price : item.price) * item.quantity).toFixed(2)}
                </span>
                {items.length > 1 && (
                  <button type="button" onClick={() => removeItem(index)} style={styles.removeBtn}>✕</button>
                )}
              </div>
            </div>
          ))}
          <button type="button" onClick={addItem} style={styles.addItemBtn}>+ Add Item</button>
          <div style={styles.total}>
            <span>Total Amount:</span>
            <span style={styles.totalAmount}>LKR {totalAmount.toFixed(2)}</span>
          </div>
<button onClick={handleSubmit} style={{ ...styles.submitBtn, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }} disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Invoice'}
          </button>        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '2rem' },
  title: { fontSize: '24px', marginBottom: '1.5rem' },
  subtitle: { fontSize: '16px', margin: '1rem 0' },
  card: { background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '700px' },
  input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', marginBottom: '0.5rem' },
  field: { marginBottom: '0.5rem' },
  creditToggleRow: { marginBottom: '0.5rem' },
  creditCheckboxLabel: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' },
  itemBlock: { marginBottom: '1rem', padding: '1rem', background: '#f9f9f9', borderRadius: '8px', position: 'relative' },
  searchWrapper: { position: 'relative', marginBottom: '0.5rem' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', zIndex: 100, maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
  dropdownItem: { padding: '10px 12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f0f0f0' },
  dropdownMeta: { fontSize: '12px', color: '#888' },
  dropdownEmpty: { padding: '10px 12px', color: '#888', fontSize: '13px' },
  itemRow: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' },
  selectedProduct: { fontSize: '13px', color: '#16a34a', flex: 2 },
  qtyInput: { width: '70px', padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' },
  dynamicPriceWrapper: { display: 'flex', flexDirection: 'column', gap: '2px' },
  dynamicPriceInput: { width: '120px', padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' },
  dynamicPriceInputOffer: { width: '120px', padding: '8px', borderRadius: '8px', border: '2px solid #f59e0b', fontSize: '14px', background: '#fef3c7', color: '#92400e', fontWeight: 'bold' },
  offerBadge: { fontSize: '11px', color: '#16a34a', fontWeight: 'bold' },
  buyingPriceHint: { fontSize: '11px', color: '#888', fontStyle: 'italic' },
  itemTotal: { fontSize: '14px', fontWeight: 'bold', color: '#4f46e5', minWidth: '100px' },
  removeBtn: { padding: '6px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  addItemBtn: { padding: '8px 16px', background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', marginBottom: '1.5rem' },
  total: { display: 'flex', justifyContent: 'space-between', padding: '1rem', background: '#f5f3ff', borderRadius: '8px', marginBottom: '1.5rem', fontSize: '16px', fontWeight: 'bold' },
  totalAmount: { color: '#4f46e5', fontSize: '20px' },
  submitBtn: { width: '100%', padding: '12px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer' },
  scanBtn: { padding: '10px 14px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  scanOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  scanModal: { background: '#fff', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' },
  bill: { background: '#fff', padding: '2rem', borderRadius: '12px', maxWidth: '500px', margin: '0 auto', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  billTop: { textAlign: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px dashed #ddd' },
  billTitle: { fontSize: '24px', margin: '0 0 0.5rem' },
  billSub: { fontSize: '14px', color: '#555', margin: '4px 0' },
  billDividerTop: { borderTop: '1px dashed #ddd', margin: '0.75rem 0' },
  billTable: { width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' },
  bth: { padding: '8px', background: '#f5f3ff', fontSize: '13px', textAlign: 'left', borderBottom: '2px solid #4f46e5' },
  btd: { padding: '8px', borderBottom: '1px solid #f0f0f0', fontSize: '13px' },
  billTotal: { display: 'flex', justifyContent: 'space-between', padding: '1rem', background: '#f5f3ff', borderRadius: '8px', fontWeight: 'bold', fontSize: '16px' },
  billTotalAmount: { color: '#4f46e5', fontSize: '20px' },
  billFooterSection: { marginTop: '1rem', textAlign: 'center' },
  billDivider: { borderTop: '1px dashed #ddd', margin: '0.75rem 0' },
  billNote: { fontSize: '12px', color: '#555', textAlign: 'left', marginBottom: '0.5rem' },
  billTerms: { fontSize: '12px', color: '#555', textAlign: 'left', paddingLeft: '1.2rem', lineHeight: '1.8' },
  billThankYou: { fontSize: '16px', fontWeight: 'bold', color: '#4f46e5', marginBottom: '4px' },
  billVisitAgain: { fontSize: '13px', color: '#888' },
  billActions: { display: 'flex', gap: '1rem', maxWidth: '500px', margin: '1.5rem auto 0', flexWrap: 'wrap' },
  printBtn: { flex: 1, padding: '10px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  whatsappBtn: { flex: 1, padding: '10px', background: '#25d366', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  addMoreBtn: { flex: 1, padding: '10px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  newBtn: { flex: 1, padding: '10px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  whatsappBox: { background: '#fff', padding: '1rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '500px', margin: '1rem auto 0' },
  whatsappTitle: { fontSize: '14px', marginBottom: '0.75rem', color: '#444' },
  whatsappRow: { display: 'flex', gap: '0.5rem' },
  whatsappInput: { flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' },
  whatsappSendBtn: { padding: '10px 16px', background: '#25d366', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  addMoreSection: { background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '500px', margin: '1.5rem auto 0' },
  specialInvoiceBanner: { background: '#f5f3ff', color: '#4f46e5', padding: '10px 14px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', marginBottom: '1rem', textAlign: 'center' },
};

export default CreateInvoice;